<template>
  <div class="rsvp-report-box">
    <div class="rsvp-report">
      <div class="form-group">
        <div class="instruction">Please select a start and end date:</div>
        <div class="row expanded">
          <div class="small-6 column padding-R">
            <div class="date-input">
              <label>Start Date:</label>
              <datepicker class="datepicker" v-model="from" format="MMMM dd, yyyy" :highlighted="highlightedDates"></datepicker>
            </div>
          </div>
          <div class="small-6 column padding-L">
            <div class="date-input end-date">
              <label>End Date:</label>
              <datepicker class="datepicker" v-model="to" format="MMMM dd, yyyy" :highlighted="highlightedDates"></datepicker>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="list-box">
      <div class="rsvp-report">
        <div class="form-group">
          <div class="download-type">
            <label :class="{'pdf':true, 'has-error':formatError}">
              <input type="checkbox" name="downloadType" v-model="pdfChecked"
                @click="formatCheck" />
              PDF
            </label>
            <label :class="{'csv':true, 'has-error':formatError}">
              <input type="checkbox" name="downloadType" v-model="csvChecked"
                @click="formatCheck" />
              CSV
            </label>
          </div>

          <div class="instruction">Please select the reports to generate:</div>

          <div class="row expanded reports">
            <div :class="['small-12 column report', reportStatus.byRating, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.byRating" />
                <span>{{ reportLabel('byRating') }}</span>
              </label>
              <select :class="{disabled:!selectedReports.byRating}"
                :disabled="!selectedReports.byRating" v-model="rating">
                <option value="">All</option>
                <option value="5">5</option>
                <option value="4">4</option>
                <option value="3">3</option>
                <option value="2">2</option>
                <option value="1">1</option>
              </select>
            </div>

            <div class="small-12 column report">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.driverRelease" />
                <span>Driver Release</span>
              </label>
            </div>
          </div>
        </div>

        <transition name="slide-down">
          <div class="error" v-show="error.show">
            <span v-show="!!error.message">{{ error.message }}</span>
            <ul v-show="error.empty.length">
              <li>Empty Result:</li>
              <li v-for="(empty,x) in error.empty" :key="x">- {{ empty }}</li>
            </ul>
          </div>
        </transition>

        <a v-bind:class="{ 'button cta success small block': true, disabled: pdf.loading }"
          href="javascript:;" @click="startProcess">
          <i class="fa fa-spinner fa-spin" v-show="pdf.loading"></i>
          <span v-show="!pdf.loading">Generate and Download Report</span>
        </a>
      </div>

      <div id="pdf-view" :class="[pdf.class]">
        <div v-for="(pageData, pageIndex) in pdf.table.data" :class="['page', pageIndex]" :key="pageIndex">
          <div class="title">{{ pdf.title }}</div>
          <div class="date-range">{{ pdf.date }}</div>
          <table>
            <thead>
              <tr>
                <th v-for="(label, x) in pdf.table.label" v-html="label" :key="x"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, index) in pageData" :key="index">
                <td v-for="(value, i) in data" :class="[i]" :key="i">{{ value }}</td>
              </tr>
            </tbody>
          </table>
          <div class="page-count">page {{ currentPage(pageIndex) }} of {{ totalPage }}</div>
        </div>

        <div :class="['page', `page${Object.keys(pdf.table.data).length + 1}`]" v-if="Object.keys(pdf.table.data).length">
          <div class="title">{{ pdf.title }}</div>
          <div class="date-range">{{ pdf.date }}</div>
          <table>
            <thead>
              <tr>
                <th v-for="label in pdf.table.label" v-html="label"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, index) in pdf.table.data[`page${Object.keys(pdf.table.data).length}`]" :key="index">
                <td v-for="(value, i) in data" :class="[i]" :key="i">{{ value }}</td>
              </tr>
            </tbody>
          </table>
          <div class="page-count">page {{ Object.keys(pdf.table.data).length }} of {{ totalPage }}</div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import moment from 'moment'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import datepicker from 'vuejs-datepicker'
import CsvExport from 'csvexport'
import featureToggle from '@/modules/api/featureToggle'

export default {
  name: 'specialize-report',
  components: { datepicker },
  data () {
    return {
      from: null,
      to: null,
      error: {
        show: false,
        message: '',
        empty: []
      },
      selectedReports: {
        byRating: false,
        driverRelease: false
      },
      reportStatus: {
        byRating: '',
        driverRelease: ''
      },
      pdf: {
        code: '',
        class: '',
        title: '',
        date: '',
        table: {
          label: [],
          data: {},
          extra: {},
          total: {}
        },
        maxRecordPerPage: 40,
        list: [],
        current: '',
        loading: false
      },
      orientation: [], // if landscape
      pdfChecked: true,
      csvChecked: false,
      rating: '',
      formatError: false,
      csv: {
        label: [],
        data: [],
        list: [],
        total: {}
      },
      isIOS: navigator.userAgent.match(/(iPad|iPhone|iPod)/g)
    }
  },
  computed: {
    show () { return this.$store.state.reports.rsvpReport.show },
    highlightedDates () {
      let self = this
      let result = { to: null, from: null }

      if (self.from !== null) { result.from = self.from }
      if (self.to !== null) { result.to = self.to }
      return result
    },
    totalPage () { return Object.keys(this.pdf.table.data).length },
    admins () { return this.$store.state.reservationNotes.admins.filter(x => x.name.toLowerCase().includes(this.searchAdmin.toLowerCase())) }
  },
  methods: {
    isValid () {
      let self = this
      self.error.show = false
      self.error.empty = []
      if (self.from === null || self.to === null) {
        self.error.show = true
        self.error.message = 'Please specify the Start and End dates.'
        return false
      }

      let from = moment(self.from)
      let to = moment(self.to)

      if (from.isAfter(to)) {
        self.error.show = true
        self.error.message = 'End date must be after Start date.'
        return false
      }

      self.formatError = false
      if (!self.pdfChecked && !self.csvChecked) {
        self.error.show = true
        self.error.message = 'Please select which file format.'
        self.formatError = true
        return false
      }

      if (!self.selectedReports.byRating && !self.selectedReports.driverRelease) {
        self.error.show = true
        self.error.message = 'Please select at least one report type.'
        return false
      }

      // if (self.selectedReports.cancellationReport && self.pdfChecked) {
      //   self.error.show = true
      //   self.error.message = 'Only CSV format is allowed for Cancellation Report.'
      //   return false
      // }

      return true;
    },
    getNewDoc () {
      let doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      })
      doc.setFont('helvetica')
      return doc
    },
    startProcess () {
      if (!this.isValid()) { return }

      this.pdf.list = []
      this.csv.list = []
      for (let report in this.selectedReports) {
        if (this.selectedReports[report]) {
          this.reportStatus[report] = 'on-queue'
          this.pdf.list.push(report)
          this.csv.list.push(report)
        }
      }

      this.pdf.loading = true
      this.generateReport()
    },
    generateReport (isCsv = false) {
      if (((!this.pdf.list.length && this.pdfChecked) || !this.pdfChecked) &&
        ((!this.csv.list.length && this.csvChecked) || !this.csvChecked)
      ) {
        this.pdf.loading = false
        return
      }

      if (isCsv) {
        console.log('Processing:', this.csv.list[0])
        this.getDataReports(this.csv.list[0])
      } else {
        console.log('Processing:', this.pdf.list[0])
        this.getDataReports(this.pdf.list[0])
      }
    },
    getDataReports (type) {
      let self = this
      self.reportStatus[type] = 'loading'
      let orientationMax = self.orientation
      self.pdf.maxRecordPerPage = orientationMax.indexOf(type) > -1 ? 25 : 40
      self.$sdk.get(self.apiParams(type).url, self.apiParams(type).data, r => {
        // console.log('---', r)
        // return
        if (r.error) {
          self.reportStatus[type] = 'has-error'
          return
        }

        self.setPdfLabel(type)
        self.pdf.table.data = {}
        self.pdf.table.extra = {}
        self.pdf.table.total = {}
        self.csv.data = []
        self.csv.total = {}

        if (r.list.length > 0) {
          self.buildPdfData(type, r.list)
        } else {
          self.error.show = true
          self.error.empty.push(self.reportLabel(type))
          self.reportStatus[type] = 'has-error'
          self.pdf.list.shift()
          self.csv.list.shift()
          self.generateReport()
        }
      })
    },
    apiParams (type) {
      let url = ''
      let start = moment(this.from)
      let end = moment(this.to)
      let data = {
        token: this.$store.state.session.sessionToken,
        since: start.format('YYYY-MM-DD'),
        until: end.format('YYYY-MM-DD')
      }

      switch (type) {
        case 'byRating':
          url = '/reports/ratings'
          data.filter = 'ratingTime'
          data.order = 'created'
          data.asc = 0
          data.value = this.rating
          break
        case 'driverRelease':
          url = '/reports/drivers/release'
          break
      }
      return { url: url, data: data }
    },
    setPdfLabel (type) {
      let start = moment(this.from)
      let end = moment(this.to)
      let dateRange = start.format('MM/DD/YYYY') + ' - ' + end.format('MM/DD/YYYY')

      this.pdf.code = type
      if (type === 'byRating') {
        this.pdf.class = 'report-rating'
        this.pdf.title = 'Opoli Driver Reviews'
        this.pdf.date = dateRange
        this.pdf.table.label = ['Date', 'Reservation ID', 'Driver ID', 'Driver Name', 'Rating', 'Reviewer ID', 'Reviewed By']
      }

      if (type === 'driverRelease') {
        this.pdf.class = 'report-release'
        this.pdf.title = 'Opoli Driver Release'
        this.pdf.date = dateRange
        this.pdf.table.label = ['Driver ID', 'Driver Name', 'RSVP ID', 'Pickup Date', 'Release Date', 'Cancellation Fee']
      }
    },
    buildPdfData (type, pdfData) {
      let self = this
      let groupCount = 0
      let page = 1

      // driverRelease
      let totalCxlFee = 0

      pdfData.forEach(function (data, index) {
        if (!self.pdf.table.data['page' + page]) {
          self.pdf.table.data['page' + page] = []
        }

        self.pdf.table.data['page' + page].push(self.buildTableData(type, data, 'pdf'))
        if (self.csvChecked) {
          self.csv.data.push(self.buildCsvData(type, data))
        }
        groupCount++

        if (type === 'driverRelease') {
          totalCxlFee += data.cancelFee
        }

        if ((index + 1) % self.pdf.maxRecordPerPage === 0 && pdfData.length !== index + 1) { page++ }
      })

      self.pdf.table.total.record = pdfData.length
      if (type === 'driverRelease') {
        self.pdf.table.total.cxlFee = self.covertNumber(totalCxlFee.toFixed(2))
      }

      if (self.pdfChecked) {
        self.processDownloads('pdf', {type: type, page: page})
      } else {
        self.processDownloads('csv', {type: type})
      }
    },
    buildTableData (type, data, format) {
      let self = this
      if (type === 'byRating') {
        let temp = {
          date: moment(data.created).format('MM/DD/YYYY  HH:mm'),
          rsvpId: data.rsvpId,
          driverId: data.driverId,
          driver: data.driverName,
          rating: data.rating,
          customerId: data.customerId,
          customer: data.customerName
        }
        if (format === 'csv') {
          temp.message = data.message
        }
        return temp
      }
      if (type === 'driverRelease') {
        return {
          id: data.driverId,
          name: `${data.firstName} ${data.lastName}`,
          rsvpId: data.rsvpId,
          pickupDate: moment.tz(data.pickupTime, 'YYYY-MM-DD@HH:mm', 'America/Los_Angeles').format('MM/DD/YYYY  HH:mm'),
          releaseDate: moment.tz(data.releaseTime, 'YYYY-MM-DD@HH:mm', 'America/Los_Angeles').format('MM/DD/YYYY  HH:mm'),
          cancellationFee: parseFloat(data.cancelFee).toFixed(2)
        }
      }
    },
    fileName (type, format) {
      let timestamp = moment(this.from).format('YYYYMMDD') + '_' + moment(this.to).format('YYYYMMDD')
      switch (type) {
        case 'byRating': return 'driver_review_' + timestamp + '.' + format
        case 'driverRelease': return 'driver_release_' + timestamp + '.' + format
      }
    },
    currentPage (page) { return page.substring(4) },
    processPdf (type, page) {
      let self = this
      let start = moment(self.from)
      let end = moment(self.to)
      let pageAsyncs  = []
      let landscapeReports = self.orientation
      let orientation = landscapeReports.indexOf(type) > -1 ? 'l' : 'p'
      let pdf = new jsPDF({ orientation: orientation, unit: 'mm', format: 'a4' })

      window.scrollTo(500, 0)
      document.body.style.overflow = 'hidden'

      setTimeout(() => {
        if (self.isIOS) page = page + 1
        for (let i = 0; i < page; i++) {
          // self.pdf.current = 'page' + (i + 1)
          pageAsyncs.push(html2canvas(document.getElementsByClassName('page' + (i + 1))[0], {
            scale: "1"
          }).then(canvas => {
            let imgData = canvas.toDataURL('image/png', 1.0)
            pdf.addImage(imgData, 'PNG', 0, 0)
            if (i + 1 !== page) { pdf.addPage() }
          }).catch(e => {
            self.reportStatus[type] = 'has-error'
            self.pdf.list.shift()
            document.body.style.overflow = 'auto'
            self.processDownloads('csv', {type: type})
            console.warn(e)
          }))
        }

        Promise.all(pageAsyncs).then(() => {
          let timestamp = start.format('YYYYMMDD') + '_' + end.format('YYYYMMDD')
          pdf.save(self.fileName(type, 'pdf'))
          self.reportStatus[type] = 'success'
          self.pdf.list.shift()
          document.body.style.overflow = 'auto'
          self.processDownloads('csv', {type: type})
        })
      }, 500)
    },
    covertNumber (value) { return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") },
    resetSelectedStatus () {
      this.selectedReports = {
        byRating: false,
        driverRelease: false
      }
    },
    resetReportStatus () {
      this.error.show = false
      this.error.empty = []
      this.reportStatus = {
        byRating: '',
        driverRelease: ''
      }
    },
    reportLabel (type) {
      switch (type) {
        case 'byRating': return 'Driver Review By Review Score'
        case 'driverRelease': return 'Driver Release'
      }
    },
    buildCsvData (type, data) {
      let column = []
      this.pdf.table.label.forEach(function(i) {
        column.push(i.replace('<br/>', ''))
      })
      if (type === 'byRating') {
        column.push('Comment')
      }
      this.csv.label = column

      let csvData = {}
      let pdfData = this.buildTableData(type, data, 'csv')

      let i = 0
      for (var key in pdfData) {
        csvData[column[i]] = pdfData[key]
        i++
      }
      return csvData
    },
    processDownloads (action, data) {
      if (action === 'pdf' && this.pdfChecked) {
        this.processPdf(data.type, data.page)
      } else if (action === 'csv' && this.csvChecked) {
        this.processCsv(data.type)
      } else {
        this.generateReport()
      }
    },
    processCsv (type) {
      const exporter = CsvExport.create({
        filename: this.fileName(type, 'csv'),
        columns: this.csv.label
      })

      // add total
      this.csv.data.push(this.buildCsvTotal(type))

      exporter.downloadCsv(this.csv.data)
      this.reportStatus[type] = 'success'
      this.csv.list.shift()

      this.generateReport(true)
    },
    buildCsvTotal (type) {
      let csvData = {}
      let totalData = this.pdf.table.total

      if (type === 'driverRelease') {
        csvData[this.csv.label[0]] = 'Total (' + totalData.record + ')'
        csvData[this.csv.label[1]] = ''
        csvData[this.csv.label[2]] = ''
        csvData[this.csv.label[3]] = ''
        csvData[this.csv.label[4]] = ''
        csvData[this.csv.label[5]] = totalData.cxlFee
      }

      return csvData
    },
    formatCheck () {
      this.error.show = false
      this.formatError = false
    },
  },
  watch: {
    show (value) {
      if (!value) { return }

      this.from = null
      this.to = null
      this.error.show = false
      this.error.empty = []

      this.resetSelectedStatus()
      this.resetReportStatus()
    },
    from (value) { this.resetReportStatus() },
    to (value) { this.resetReportStatus() },
    'selectedReports.byRating' (value) { this.resetReportStatus() },
    'selectedReports.driverRelease' (value) { this.resetReportStatus() },
  }
}
</script>

<style lang="scss" scoped>
.rsvp-report-box {
  transform-origin: top center;
  position: relative;
  width: 100%;
}
.rsvp-report {
  border-radius: 3px;
  padding: 0 20px 20px;

  & .title {
    text-align: center;
    font-weight: 700;
    margin-bottom: 20px;
    font-size: 18px;
  }

  & .date-input {
    margin-bottom: 10px;

    & label {
      font-size: 13px;
      color: #999;
      display: block;
    }
    select{
      padding: 10px;
      height: 46px;
      outline: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      border-color: #cacaca;
      font-size: 14px;
      color: #333;
      background-image: url('/assets/img/dropdown-arrow.png');
      background-repeat: no-repeat;
      background-position: right 50%;

      &[disabled]{ background-color: #ebebe4; }
    }
  }

  .reports{
    max-height: 350px;
    margin-bottom: 10px;
    overflow: auto;
    clear: both;
    background: #fafafa;
    border: 1px solid #ddd;
    position: relative;

    .report{
      position: relative;
      z-index: 1;

      &:hover{ background:#f1f1f1; }
      &.processing{
        background: rgba(0,0,0, .7);
        input{ opacity: .5; }
      }
      select{
        margin-left: 28px;
        margin-bottom: 10px;
        width: calc(100% - 43px);
        border-color: #999;
        font-size: 13px;
        color: #333;
        padding: 3px 5px;
        transition: all .2s ease;

        &.disabled{
          background: #fafafa;
          border-color: #ccc;
        }
        option:disabled{ background: #eaeaea; }
      }
      &.on-queue, &.loading, &.has-error, &.success{
        z-index: 3;
        box-shadow: 0 0 2px rgba(0,0,0, .4);
      }
      &.on-queue{ background: #fff; }
      &.loading{
        color: #775913;
        background: #f1ce7e;
      }
      &.success{
        color: #0f693a;
        background: #a9f7ce;
      }
      &.has-error{
        color: #b93030;
        background: #fbb0b0;
      }
    }
  }
}

.filter{
  list-style: none;
  margin: 0 0 5px;
  padding: 0;
  font-size: 13px;
  display: flex;

  li{
    margin: 2px 0;
    width: 100%;
  }
  label{ cursor: pointer;}
  input{
    display: inline-block;
    margin: 0 3px 0 0;
    width: auto;
    vertical-align: middle;
  }
}

.download-type{
  float: right;
  width: 150px;
  text-align: right;
  padding-bottom: 5px;

  label{
    cursor: pointer;
    display: inline-block;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    padding: 2px 7px;
    border:1px solid #ddd;

    &.pdf{ color: #c54d4d; }
    &.csv{ color: #7ad275; }
    &.has-error{ border: 1px solid #d00; }
    input{
      display: inline;
      margin-top: 0;
      width: auto;
      vertical-align: -1px;
    }
  }
}

.list-box{
  overflow: hidden;
  position: relative;
  padding-bottom: 20px;
}

.error {
  background-color: #F25E0D;
  font-size: 14px;
  text-align: center;
  color: #fff;
  border-radius: 3px;
  padding: 5px;
  margin-bottom: 15px;
  transform-origin: top center;

  ul{
    list-style: none;
    margin: 0;
    padding: 10px;
    text-align: left;

    li:first-child{
      font-weight: 600;
      font-size: 14px;
      line-height: 1;
      margin-bottom: 2px;
    }
    li{
      font-size: 13px;
    }
  }
}

.form-group {
  display: block;

  & .instruction {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: 600;
    color: #777;
  }
}

.checkbox {
  display: block;
  font-size: 13px;
  cursor: pointer;
  padding: 5px 10px;
  & > * {
    display: inline-block;
    vertical-align: middle;
    margin: 0;
    width: auto;
  }
  input { margin-right: 3px; }
}
.padding-R { padding-right: 7px; }
.padding-L { padding-left: 7px; }

#pdf-view{
  position: absolute;
  top: 100%;
  left: 0;
  width: 793px;
  height: 1122px;
  background: #fff;

  .page{
    padding: 30px 40px 30px 30px;
    position: relative;
    width: 100%;
    height: 100%;

    .page-count{
      position: absolute;
      bottom: 10px;
      right: 30px;
      color: #aaa;
      font-size: 12px;
      text-align: right;
      font-style: italic;
    }
  }
  .title{
    text-align: center;
    font-weight: 600;
    font-size: 21px;
  }
  .date-range{
    text-align: center;
    font-style: italic;
    font-size: 18px;
    margin-bottom: 20px;
    color: #555;
  }

  table{
    width: 100%;
    font-size: 12px;
    border-collapse: collapse;

    thead{
      font-weight: 600;
      tr:last-child th{ border-bottom: 1px solid #333; }
    }
    tbody{
      tr{
        &.subtotal, &.total{ font-weight: 700; }
        &.total{
          color: #000;
          font-size: 12px;
        }
      }
      td{ padding: 2px 3px; }
    }
  }
}

.report-rating{
  height: 1122px!important;
  width: 793px!important;
  table{
    tbody tr.total{ border-top: 1px solid #333; }
    .date, .rsvpId, .driverId, .driver, .customerId, .customer, .rating, .message{ text-align: center; }
  }
}

.report-release{
  table{
    tbody td{
      text-align: center;
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active { transition: all .3s ease; }
.slide-down-enter, .slide-down-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .end-date {
    /deep/ .vdp-datepicker__calendar {
      right: 1px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Reports/ProviderRatingReport.vue